import {
  Action,
  action,
  Computed,
  computed,
  thunk,
  Thunk,
  thunkOn,
  ThunkOn,
} from "easy-peasy";
import { useStoreState } from "../hooks/ep";
import { Injections } from "./store-injections";
import { StoreModel } from "./model";
import {
  makeAuthorizedGetRequestToBackend,
  makeUrl,
} from "../helpers/backendApi";
import { toaster } from "../toaster";

type MailtoInfo = {
  entityType: "student" | "enrollment" | "teacherCourse" | "leadsQueue";
  entityId: number | string;
  emailTemplateId: string;
};

type SentEmailContent = {
  id: number;
  subject: string;
  body: string;
};

export interface MiscModel {
  NAME: string;
  //
  resetData: Thunk<MiscModel, void, Injections, StoreModel>;
  onLogin: ThunkOn<MiscModel, Injections, StoreModel>;
  onResetMeData: ThunkOn<MiscModel, Injections, StoreModel>;
  //
  fullScreenModeEnabled: boolean;
  enableFullScreenMode: Action<MiscModel>;
  disableFullScreenMode: Action<MiscModel>;
  toggleFullScreenMode: Action<MiscModel>;
  //
  ra_payload: RA_Payload | null;
  ra_openModal: Action<MiscModel, RA_Payload>;
  ra_closeModal: Action<MiscModel>;
  //
  buzzAndGeniusDataRefreshedAt: string;
  setBuzzAndGeniusDataRefreshedAt: Action<MiscModel, string>;
  //
  netsuiteDataRefreshedAt: string;
  setNetsuiteDataRefreshedAt: Action<MiscModel, string>;
  //
  handleFetchMailtoLink: Thunk<MiscModel, MailtoInfo, Injections, StoreModel>;
  //
  mailtoInfoForAlert: MailtoInfo;
  setMailtoInfoForAlert: Action<MiscModel, MailtoInfo>;
  //
  mailtoLinkForAlert: string | null;
  setMailtoLinkForAlert: Action<MiscModel, string | null>;
  isMailtoAlertOpen: Computed<MiscModel, boolean, StoreModel>;
  //
  sentEmailContent: SentEmailContent;
  setSentEmailContent: Action<MiscModel, SentEmailContent>;
  //
  mailtoInfoForDrawer: MailtoInfo;
  setMailtoInfoForDrawer: Action<MiscModel, MailtoInfo>;
  //
  mailtoLinkForDrawer: string | null;
  setMailtoLinkForDrawer: Action<MiscModel, string | null>;
  isMailtoDrawerOpen: Computed<MiscModel, boolean, StoreModel>;
  //
  handleOpenMailtoLinkInsideApp: Thunk<MiscModel, void, Injections, StoreModel>;
  handleOpenMailtoLinkOutsideApp: Thunk<
    MiscModel,
    void,
    Injections,
    StoreModel
  >;
  handleCloseMailtoLinkOverlays: Thunk<MiscModel, void, Injections, StoreModel>;
  handleCloseSentEmailViewerOverlays: Thunk<
    MiscModel,
    void,
    Injections,
    StoreModel
  >;
}

export interface RA_Payload {
  actionName: string;
  snapshot: any;
}

const DEFAULT_MAILTO_INFO_VALUE = {
  entityType: null,
  entityId: null,
  emailTemplateId: null,
};
const DEFAULT_MAILTO_LINK_VALUE = null;
const DEFAULT_SENT_EMAIL_CONTENT = null;
// const DEFAULT_MAILTO_LINK_VALUE =
//   "mailto:leticiabaut12%40gmail.com?cc=&bcc=carbon%40customax.ai&subject=LSS%20Check-In%20%20%7C%20%20Jimena%20Catalan%20Bautista&body=Dear%20Leticia%2C%0A%0AI%20wanted%20to%20reach%20out%20to%20let%20you%20know%20that%20your%20student%2C%20Jimena%2C%20may%20be%20struggling%20in%20the%20following%207%20courses%3A%0A%0ASpanish%203%20OL%20v5%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%201%25%20%20%7C%20%20Last%20Submission%3A%20108%20days%20ago%20%20%7C%20%20Current%20Grade%3A%20100%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%20%20%20%20%E2%80%A2%20MIA%3A%20no%20assignments%20submitted%20in%20past%202%20weeks%0A%0AHonors%20American%20History%20OL%20v3%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%203%25%20%20%7C%20%20Last%20Submission%3A%2010%20days%20ago%20%20%7C%20%20Current%20Grade%3A%20100%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%0AAlgebra%202%20OL%20v3%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%208%25%20%20%7C%20%20Last%20Submission%3A%2013%20days%20ago%20%20%7C%20%20Current%20Grade%3A%2094%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%0AChemistry%20OL%20v4%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%209%25%20%20%7C%20%20Last%20Submission%3A%2010%20days%20ago%20%20%7C%20%20Current%20Grade%3A%2092%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%0AHonors%20American%20Literature%20OL%20v4%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%2013%25%20%20%7C%20%20Last%20Submission%3A%2049%20days%20ago%20%20%7C%20%20Current%20Grade%3A%2094%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%20%20%20%20%E2%80%A2%20MIA%3A%20no%20assignments%20submitted%20in%20past%202%20weeks%0A%0AJunior%20Seminar%20OL%20v2%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2037%25%20%20%7C%20%20Actual%20Completion%3A%200%25%20%20%7C%20%20Last%20Submission%3A%20109%20days%20ago%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%20%20%20%20%E2%80%A2%20Slow%20Start%3A%20behind%20pace%20%2B%20course%20recently%20started%0A%20%20%20%20%E2%80%A2%20MIA%3A%20no%20assignments%20submitted%20in%20past%202%20weeks%0A%0A11th%20Grade%20PE%20OL%20v1%20A%0A%20%20%20%20%E2%86%B3%20Expected%20Completion%3A%2074%25%20%20%7C%20%20Actual%20Completion%3A%2064%25%20%20%7C%20%20Last%20Submission%3A%2010%20days%20ago%20%20%7C%20%20Current%20Grade%3A%20100%25%0A%20%20%20%20%E2%80%A2%20Behind%20Pace%3A%20significantly%20behind%20assignment%20submission%20pace%0A%0AIt%20is%20still%20the%20middle%20of%20the%20term%20and%20Jimena%20can%20certainly%20get%20back%20on%20track%2C%20but%20we%20wanted%20to%20flag%20this%20to%20you%20so%20that%20you%20can%20help%20your%20student%20be%20successful%20this%20term.%20%20%0A%0AWarm%20Regards%2C%0AAustin%20Ray%0A%0ALaurel%20Springs%20Schools%20%20%7C%20%20www.laurelsprings.com";

const miscModel: MiscModel = {
  NAME: "misc",
  fullScreenModeEnabled: false,
  enableFullScreenMode: action((state) => {
    state.fullScreenModeEnabled = true;
  }),
  disableFullScreenMode: action((state) => {
    state.fullScreenModeEnabled = false;
  }),
  toggleFullScreenMode: action((state) => {
    state.fullScreenModeEnabled = !state.fullScreenModeEnabled;
  }),
  //
  ra_payload: null,
  ra_openModal: action((state, ra_payload) => {
    state.ra_payload = ra_payload;
  }),
  ra_closeModal: action((state) => {
    state.ra_payload = null;
  }),
  //
  resetData: thunk((actions) => {
    actions.disableFullScreenMode();
  }),
  onLogin: thunkOn(
    (__, storeActions) => storeActions.me.receiveInitialData,
    (actions, target, { getStoreState }) => {
      actions.resetData();
    }
  ),
  onResetMeData: thunkOn(
    (__, storeActions) => storeActions.me.resetData,
    (actions) => {
      actions.resetData();
    }
  ),
  //
  buzzAndGeniusDataRefreshedAt: "",
  setBuzzAndGeniusDataRefreshedAt: action((state, value) => {
    state.buzzAndGeniusDataRefreshedAt = value;
  }),
  //
  netsuiteDataRefreshedAt: "",
  setNetsuiteDataRefreshedAt: action((state, value) => {
    state.netsuiteDataRefreshedAt = value;
  }),
  //
  handleFetchMailtoLink: thunk(
    async (actions, { entityType, entityId, emailTemplateId }) => {
      try {
        const resp = await makeAuthorizedGetRequestToBackend({
          url: makeUrl(
            `ecp/mailto_link/${emailTemplateId}/${entityType}/${entityId}`
          ),
          axiosConfig: {},
        });

        actions.setMailtoLinkForAlert(resp.data.mailto_link);
        actions.setMailtoInfoForAlert({
          entityType,
          entityId,
          emailTemplateId,
        });
        // window.open(resp.data.mailto_link);
      } catch (e) {
        toaster.error("Something went wrong. Please reload and try again");
      }
    }
  ),
  //
  mailtoInfoForAlert: DEFAULT_MAILTO_INFO_VALUE,
  setMailtoInfoForAlert: action((state, value) => {
    state.mailtoInfoForAlert = value;
  }),
  //
  mailtoLinkForAlert: DEFAULT_MAILTO_LINK_VALUE,
  setMailtoLinkForAlert: action((state, value) => {
    state.mailtoLinkForAlert = value;
  }),
  isMailtoAlertOpen: computed(
    [(s) => s.mailtoLinkForAlert, (s) => s.mailtoLinkForDrawer],
    (forAlert, forDrawer) => !!forAlert && !forDrawer
  ),
  //
  mailtoInfoForDrawer: DEFAULT_MAILTO_INFO_VALUE,
  setMailtoInfoForDrawer: action((state, value) => {
    state.mailtoInfoForDrawer = value;
  }),
  //
  mailtoLinkForDrawer: DEFAULT_MAILTO_LINK_VALUE,
  setMailtoLinkForDrawer: action((state, value) => {
    state.mailtoLinkForDrawer = value;
  }),
  isMailtoDrawerOpen: computed([(s) => s.mailtoLinkForDrawer], (v) => !!v),
  //
  handleOpenMailtoLinkInsideApp: thunk((actions, payload, { getState }) => {
    const s = getState();
    const mailtoLink = s.mailtoLinkForAlert;
    const mailtoInfo = s.mailtoInfoForAlert;
    actions.setMailtoLinkForDrawer(mailtoLink);
    actions.setMailtoInfoForDrawer(mailtoInfo);
  }),
  handleOpenMailtoLinkOutsideApp: thunk((actions, payload, { getState }) => {
    window.open(getState().mailtoLinkForAlert);
    actions.setMailtoLinkForAlert(DEFAULT_MAILTO_LINK_VALUE);
    actions.setMailtoInfoForAlert(DEFAULT_MAILTO_INFO_VALUE);
  }),
  handleCloseMailtoLinkOverlays: thunk((actions, payload, { getState }) => {
    actions.setMailtoLinkForAlert(DEFAULT_MAILTO_LINK_VALUE);
    actions.setMailtoInfoForAlert(DEFAULT_MAILTO_INFO_VALUE);
    //
    actions.setMailtoLinkForDrawer(DEFAULT_MAILTO_LINK_VALUE);
    actions.setMailtoInfoForDrawer(DEFAULT_MAILTO_INFO_VALUE);
  }),
  //
  sentEmailContent: DEFAULT_SENT_EMAIL_CONTENT,
  setSentEmailContent: action((state, value) => {
    state.sentEmailContent = value;
  }),
  handleCloseSentEmailViewerOverlays: thunk(
    (actions, payload, { getState }) => {
      actions.setSentEmailContent(DEFAULT_SENT_EMAIL_CONTENT);
    }
  ),
};

export const useFullScreenModeEnabled = () =>
  useStoreState((s) => s.misc.fullScreenModeEnabled);

export default miscModel;
